import { useDisclosure } from '@mantine/hooks';
import { Modal, Button, List, Stack, Anchor, Checkbox } from '@mantine/core';
import { useEffect, useState } from 'react';
import mixpanel from 'mixpanel-browser';
import { TERMS_OF_USE_URL } from 'src/constants';

export function TermsOfUseModal() {
  const [opened, { open, close }] = useDisclosure(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const siteName = 'this site';
  const storageKey = 'aprioriTermsLastVisit';

  useEffect(() => {
    const lastVisit = localStorage.getItem(storageKey);
    const oneWeekInMilliseconds = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

    if (
      !lastVisit ||
      Date.now() - new Date(lastVisit).getTime() >= oneWeekInMilliseconds
    ) {
      // If it's the user's first visit or a week has passed since the last visit
      open();
    }
  }, []);

  /**
   * Render list item
   * @param text - text to show
   * @returns JSX
   */
  const renderListItem = (text: string) => {
    return (
      <List.Item maw="95%" c="var(--mantine-color-text-45)">
        {text}
      </List.Item>
    );
  };

  const handleAgreeClick = () => {
    // Update the last visit timestamp
    localStorage.setItem(storageKey, new Date().toISOString());
    mixpanel.track('Terms of Service Agreed');
    close();
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title="Terms of Service"
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
      centered
      classNames={{
        title: 'text-[24px] font-[700] mx-auto',
      }}
    >
      <Checkbox
        fz={14}
        mb={12}
        label={
          <>
            By clicking this checkbox, I hereby agree to aPriori’s{' '}
            <Anchor target="_blank" href={TERMS_OF_USE_URL} c="aprioriPurple.0">
              TERMS OF SERVICE
            </Anchor>{' '}
            and agree to be bound by them. I also agree and warrant the
            following:
          </>
        }
        classNames={{
          body: 'items-center',
        }}
        checked={isAgreed}
        onChange={(event) => setIsAgreed(event.currentTarget.checked)}
      />
      <List
        py={24}
        px={16}
        c="var(--mantine-color-main-2)"
        fz={14}
        fw={400}
        className="border border-solid border-white/[0.14] rounded-[16px]"
      >
        <Stack gap={10}>
          {renderListItem(`You are not accessing ${siteName} from a Restricted Territory as set
              forth in the Terms of Service.`)}
          {renderListItem(`You are not a person or entity who is a resident of, or is
                  located, incorporated or has a registered agent, in the United
                  States of America or a Restricted Territory.`)}
          {renderListItem(`You will not in the future access ${siteName} or otherwise trade on
              ${siteName} while located within the United States of America or a Restricted Territory.`)}
          {renderListItem(
            `You are not using, and will not in the future use, a VPN to disguise your location to access ${siteName}.`
          )}
        </Stack>
      </List>
      <Button
        mt={24}
        w="100%"
        size="lg"
        disabled={!isAgreed}
        onClick={handleAgreeClick}
      >
        Agree to Terms
      </Button>
    </Modal>
  );
}
